import React, { useState } from 'react';
import InstructionImagesSlider from './InstructionImagesSlider';

/**
 * CredentialsForm - Handles username/password input and validation
 * for external learning services (CodeCombat, Duolingo, Khan Academy)
 */
const CredentialsForm = ({
  activityType,
  activityName,
  configFields,
  onValidate,
  isValidating,
  error,
  success,
  isTestMode,
  initialValues = {},
  submitButtonText = 'Chọn Khóa Học'
}) => {
  const [credentials, setCredentials] = useState(initialValues);
  const [fieldErrors, setFieldErrors] = useState({});

  // Validate username field for Duolingo - must not be an email
  // activityType can be 'duolingo-english', 'duolingo-music', etc. or just 'duolingo'
  const isDuolingoActivity = activityType === 'duolingo' || activityType?.startsWith('duolingo-');

  const validateDuolingoUsername = (value) => {
    if (isDuolingoActivity && value && value.includes('@')) {
      return 'Vui lòng nhập username';
    }
    return null;
  };

  // Check if all required fields are filled and valid
  const hasAllRequiredFields = () => {
    const allFieldsFilled = configFields
      .filter(field => field.required && field.type !== 'course_selection')
      .every(field => credentials[field.name]?.trim());

    // Also check for field-level validation errors
    const hasNoErrors = Object.values(fieldErrors).every(err => !err);

    return allFieldsFilled && hasNoErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onValidate(credentials);
  };

  const handleChange = (fieldName, value) => {
    setCredentials(prev => ({
      ...prev,
      [fieldName]: value
    }));

    // Validate Duolingo username field
    if (fieldName === 'username') {
      const error = validateDuolingoUsername(value);
      setFieldErrors(prev => ({
        ...prev,
        [fieldName]: error
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit} {...(isTestMode && { 'data-testid': 'credentials-form' })}>
      {/* Configuration Fields */}
      {configFields.map((field) => {
        if (field.type === 'course_selection') return null;

        return (
          <div key={field.name} className="mb-4">
            {/* Info Box (if applicable) */}
            {field.info_box && (
              <div className="alert alert-info border-info mb-4" role="alert">
                <div className="d-flex">
                  <div className="flex-shrink-0 me-2">
                    <i className="bi bi-info-circle"></i>
                  </div>
                  <div>
                    <h6 className="alert-heading fw-semibold mb-1">
                      {field.info_box.title}
                    </h6>
                    <p className="mb-0 small">{field.info_box.message}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Field Input */}
            <label
              htmlFor={`credential-${field.name}`}
              className="form-label fw-semibold"
            >
              {field.label}
            </label>

            {field.type === 'select' ? (
              // Select Field
              <select
                id={`credential-${field.name}`}
                className={`form-select ${error ? 'is-invalid' : ''}`}
                value={credentials[field.name] || field.default || ''}
                onChange={(e) => handleChange(field.name, e.target.value)}
                required={field.required}
                data-field-name={field.name}
                {...(isTestMode && { 'data-testid': `credential-${field.name}` })}
              >
                {field.options?.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : (
              // Text/Password Field
              <input
                type={field.type === 'password' ? 'password' : 'text'}
                id={`credential-${field.name}`}
                className={`form-control ${error ? 'is-invalid' : ''} ${success ? 'is-valid' : ''} ${fieldErrors[field.name] ? 'is-invalid' : ''}`}
                placeholder={field.placeholder}
                value={credentials[field.name] || ''}
                onChange={(e) => handleChange(field.name, e.target.value)}
                required={field.required}
                disabled={isValidating}
                data-field-name={field.name}
                {...(isTestMode && { 'data-testid': `credential-${field.name}` })}
              />
            )}

            {/* Field-level validation error */}
            {fieldErrors[field.name] && (
              <div
                className="invalid-feedback d-block"
                {...(isTestMode && { 'data-testid': `${field.name}-error` })}
              >
                {fieldErrors[field.name]}
              </div>
            )}

            {field.help_text && (
              <small className="form-text text-muted d-block mt-1">
                {field.help_text}
              </small>
            )}

            {/* Instruction Images Slider (if applicable) */}
            {field.instruction_images && (
              <InstructionImagesSlider
                title={field.instruction_images.title}
                images={field.instruction_images.images}
                isTestMode={isTestMode}
              />
            )}
          </div>
        );
      })}

      {/* Error Message */}
      {error && (
        <div
          className="alert alert-danger"
          role="alert"
          {...(isTestMode && { 'data-testid': 'credentials-error' })}
        >
          {error}
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div
          className="alert alert-success"
          role="alert"
          {...(isTestMode && { 'data-testid': 'credentials-success' })}
        >
          {success}
        </div>
      )}

      {/* Submit Button */}
      <div className="d-flex justify-content-end gap-3 pt-3">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isValidating || !hasAllRequiredFields()}
          {...(isTestMode && { 'data-testid': 'validate-credentials-btn' })}
        >
          {isValidating ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Đang xác thực...</span>
              </span>
              Đang xác thực...
            </>
          ) : (
            submitButtonText
          )}
        </button>
      </div>
    </form>
  );
};

export default CredentialsForm;
